import React, { useState, useEffect, useCallback } from "react";
import { Page, Grid, Card, Table, Button } from "tabler-react";
import SiteWrapper from "../../SiteWrapper";
import * as ticketService from "../../store/services/ticketServices";

const STATUS_LABELS = {
    open: "Open",
    waiting: "Waiting",
    on_hold: "On Hold",
    closed: "Closed",
};

const STATUS_COLORS = {
    open: { background: "#e8f5f5", color: "#2B9A99" },
    waiting: { background: "#fff8e1", color: "#f6a821" },
    on_hold: { background: "#f0f0f0", color: "#666" },
    closed: { background: "#fce8e8", color: "#cd201f" },
};

const PRIORITY_LABELS = {
    low: "Low",
    normal: "Normal",
    high: "High",
    urgent: "Urgent",
};

const PRIORITY_COLORS = {
    low: "#9aa0ac",
    normal: "#2B9A99",
    high: "#f6a821",
    urgent: "#cd201f",
};

function Badge({ value, labels, colors }) {
    const style = {
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        background: typeof colors[value] === "object" ? colors[value].background : "#f0fafa",
        color: typeof colors[value] === "object" ? colors[value].color : colors[value] || "#333",
    };
    return <span style={style}>{labels[value] || value}</span>;
}

function Tickets(props) {
    const [user] = useState(
        (localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))) || {}
    );
    const isStaff = user?.type === "Super" || user?.type === "Admin";

    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");

    const load = useCallback((page, status, priority) => {
        setLoading(true);
        ticketService
            .getTickets(page, status, priority)
            .then((res) => {
                const data = res.data?.data;
                setTickets(data?.data || []);
                setTotalPage(data?.last_page || 1);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        load(currentPage, statusFilter, priorityFilter);
    }, [currentPage, statusFilter, priorityFilter, load]);

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handlePriorityFilter = (e) => {
        setPriorityFilter(e.target.value);
        setCurrentPage(1);
    };

    const formatDate = (ts) => {
        if (!ts) return "—";
        const d = new Date(ts);
        return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getFullYear()).slice(-2)}`;
    };

    return (
        <SiteWrapper loading={loading}>
            <Page.Content>
                <div className="page-header">
                    <h1 className="page-title">Support Tickets</h1>
                    <div className="page-options">
                        <Button
                            className="btn-mm"
                            onClick={() => props.history.push("/tickets/new")}
                        >
                            New Ticket
                        </Button>
                    </div>
                </div>
                <Grid.Row>
                    <Grid.Col>
                        <Card>
                            <Card.Header>
                                <Card.Title>Tickets</Card.Title>
                                <Card.Options>
                                    <select
                                        className="form-control form-control-sm"
                                        value={statusFilter}
                                        onChange={handleStatusFilter}
                                        style={{ marginRight: 8, width: "auto" }}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="open">Open</option>
                                        <option value="waiting">Waiting</option>
                                        <option value="on_hold">On Hold</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                    <select
                                        className="form-control form-control-sm"
                                        value={priorityFilter}
                                        onChange={handlePriorityFilter}
                                        style={{ width: "auto" }}
                                    >
                                        <option value="">All Priorities</option>
                                        <option value="low">Low</option>
                                        <option value="normal">Normal</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </Card.Options>
                            </Card.Header>
                            {tickets.length > 0 ? (
                                <Table
                                    cards={true}
                                    striped={true}
                                    responsive={true}
                                    className="table card-table table-vcenter datatable"
                                >
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColHeader>Subject</Table.ColHeader>
                                            {isStaff && <Table.ColHeader>From</Table.ColHeader>}
                                            <Table.ColHeader>Category</Table.ColHeader>
                                            <Table.ColHeader>Priority</Table.ColHeader>
                                            <Table.ColHeader>Status</Table.ColHeader>
                                            <Table.ColHeader>Date</Table.ColHeader>
                                            <Table.ColHeader />
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {tickets.map((ticket) => (
                                            <Table.Row key={ticket.id}>
                                                <Table.Col>
                                                    <span style={{ fontWeight: ticket.unread_owner || ticket.unread_staff ? 700 : 400 }}>
                                                        {ticket.subject}
                                                    </span>
                                                </Table.Col>
                                                {isStaff && (
                                                    <Table.Col>
                                                        <div>{ticket.owner?.name} {ticket.owner?.last_name}</div>
                                                        <div style={{ fontSize: 11, color: "#9aa0ac" }}>{ticket.owner?.email}</div>
                                                    </Table.Col>
                                                )}
                                                <Table.Col style={{ textTransform: "capitalize" }}>
                                                    {ticket.category}
                                                </Table.Col>
                                                <Table.Col>
                                                    <span style={{ fontWeight: 600, color: PRIORITY_COLORS[ticket.priority] || "#333" }}>
                                                        {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                                                    </span>
                                                </Table.Col>
                                                <Table.Col>
                                                    <Badge value={ticket.status} labels={STATUS_LABELS} colors={STATUS_COLORS} />
                                                </Table.Col>
                                                <Table.Col>{formatDate(ticket.created_at)}</Table.Col>
                                                <Table.Col>
                                                    <Button
                                                        color="secondary"
                                                        size="sm"
                                                        onClick={() => props.history.push(`/tickets/${ticket.id}`)}
                                                    >
                                                        View
                                                    </Button>
                                                </Table.Col>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            ) : (
                                <Card.Body className="align-items-center justify-content-center">
                                    <h4 className="h4 mt-4 text-center" style={{ color: "#000", marginBottom: 20 }}>
                                        No Tickets
                                    </h4>
                                    <div className="detail mb-5">No support tickets found.</div>
                                </Card.Body>
                            )}
                            <Card.Footer className="justify-between">
                                <Button
                                    color="secondary"
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </Button>
                                <div>{currentPage} of {totalPage}</div>
                                <Button
                                    color="secondary"
                                    onClick={() => setCurrentPage((p) => Math.min(totalPage, p + 1))}
                                    disabled={currentPage >= totalPage}
                                >
                                    Next
                                </Button>
                            </Card.Footer>
                        </Card>
                    </Grid.Col>
                </Grid.Row>
            </Page.Content>
        </SiteWrapper>
    );
}

export default Tickets;
